import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { cx } from '@/styles/muiTheme';

import type { WscStreamStatus } from '../types';

/**
 * Colour per stream status.
 *
 * The seven statuses are three groups, and the palette says which is which
 * before the label is read:
 *
 *  - *inert* — PENDING and CANCELED never had, or no longer have, anything
 *    running. Muted text with a grey dot, which is what the design uses for its
 *    non-running rows.
 *  - *in flight* — SCHEDULED, LAUNCHING, RUNNING are progressively "more live":
 *    blue while queued, gold while provisioning, green once the feed is up.
 *  - *outcome* — ENDED finished normally, so it reads as ordinary foreground
 *    rather than muted; FAILED is the only red on the row.
 *
 * ENDED and CANCELED are deliberately different weights. Both are terminal, but
 * one means "this ran" and the other means "this never did", and an operator
 * scanning a game's history needs that distinction more than they need the two
 * to match.
 */
const STATUS_COLOR: Record<WscStreamStatus, { fg: string; dot: string }> = {
  PENDING: { fg: cx.text.muted, dot: cx.border.default },
  SCHEDULED: { fg: cx.blue.hover, dot: cx.blue.hover },
  LAUNCHING: { fg: cx.gold, dot: cx.gold },
  RUNNING: { fg: cx.green, dot: cx.green },
  ENDED: { fg: cx.text.secondary, dot: cx.border.default },
  CANCELED: { fg: cx.text.muted, dot: cx.border.default },
  FAILED: { fg: cx.red.main, dot: cx.red.main },
};

interface Props {
  status: WscStreamStatus;
}

/**
 * The pill from the design's LIVE badge, applied per stream status: outlined
 * capsule, 6px dot, tracked-out monospace label.
 *
 * Border and fill are derived from the foreground with `alpha` rather than
 * given their own tokens. The design pairs `red.main` with `red.deep` for its
 * one pill, but `deep` exists only for red — deriving keeps all seven statuses
 * on the same visual rule instead of making six of them special cases.
 */
export function WscStreamStatusPill({ status }: Props) {
  const { fg, dot } = STATUS_COLOR[status];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        height: 24,
        px: '10px',
        borderRadius: '100px',
        border: '1px solid',
        borderColor: alpha(fg, 0.4),
        bgcolor: alpha(fg, 0.12),
        color: fg,
        fontFamily: cx.fontMono,
        fontSize: 11,
        lineHeight: '16px',
        letterSpacing: '1px',
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', bgcolor: dot }}
      />
      {status}
    </Box>
  );
}
